// In your hooks file or wherever fetchAndProcessReviewData is defined

import {
  getCPQProductData,
  getCPQProductRules,
  getCPQPricebooks,
  getCPQPriceModel,
  getCPQConsumptionSchedule,
  getCPQDiscountSchedule,
} from 'services/cpq';
import { getProductsFromDb, compareProducts } from 'services/product';
import { getRCProductAttributes } from 'services/rc';
import {
  attachDataByCanonicalKey,
  matchPricingModelsToProducts,
  matchConsumptionToProducts,
  matchDiscountSchedulesToProducts,
} from 'layouts/commit/utils/productDataHelper';
import { getRCPricebooks, getRCProductModel } from './rc';

/**
 * Fetches and processes all data required for the product review stage.
 * @param {object} params - The parameters for fetching data.
 * @returns {Promise<object>} An object containing the processed data or an error.
 */
export const fetchAndProcessReviewData = async ({ orgId, selectedProducts, isRevenue, databaseMajorVersions }) => {
  // Guard clause remains essential
  if (!selectedProducts || selectedProducts.length === 0) {
    return { data: [], errors: [], fetchErrors: [] };
  }

  try {
    // --- STAGE 1: Fetch Essential Base Data ---
    let selectedData;
    let databaseData;

    const baseProductFetcher = isRevenue
      ? getRCProductModel(orgId, selectedProducts, true)
      : getCPQProductData(orgId, selectedProducts, true);

    const databaseFetcher =
      databaseMajorVersions.length > 0
        ? Promise.resolve({ products: databaseMajorVersions })
        : getProductsFromDb(orgId, selectedProducts);

    try {
      // Use a standard Promise.all for essential data. If either of these fail, we can't continue.
      const [selectedResponse, databaseResponse] = await Promise.all([baseProductFetcher, databaseFetcher]);
      selectedData = selectedResponse?.products || [];
      databaseData = databaseResponse?.products || [];
    } catch (baseError) {
      console.error('Critical error fetching base product data:', baseError);
      throw new Error(`Failed to fetch essential product data: ${baseError.message}`);
    }

    if (selectedData.length === 0) {
      console.warn('No products returned from Salesforce. Comparison will be empty.');
      // We can still compare the DB data against an empty SF array if needed, so we continue.
    }

    // --- STAGE 2: Fetch Related Data in Parallel and Handle Failures Gracefully ---
    const relatedDataPromises = isRevenue
      ? [getRCProductAttributes(orgId, selectedProducts), getRCPricebooks(orgId, selectedProducts)]
      : [
          getCPQProductRules(orgId, selectedProducts),
          getCPQPricebooks(orgId, selectedProducts),
          getCPQPriceModel(orgId, selectedProducts),
          getCPQConsumptionSchedule(orgId, selectedProducts),
          getCPQDiscountSchedule(orgId, selectedProducts),
        ];

    const relatedResults = await Promise.allSettled(relatedDataPromises);
    const fetchErrors = [];

    // --- Attach the related data that was successfully fetched ---
    if (isRevenue) {
      const [attributesResult, pricebooksResult] = relatedResults;
      if (attributesResult.status === 'fulfilled') {
        const revenueData = attributesResult.value.attributes || [];
        const revenueMap = new Map(revenueData.map((p) => [p.productName, p]));
        selectedData = selectedData.map((sfProduct) => {
          const match = revenueMap.get(sfProduct.name);
          return match ? { ...sfProduct, related_data: { ...(sfProduct.related_data || {}), ...match } } : sfProduct;
        });
      } else {
        fetchErrors.push({ endpoint: 'RC Attributes', reason: attributesResult.reason.message });
      }

      console.log('[Revenue] pricebooksResult', pricebooksResult);

      if (pricebooksResult.status === 'fulfilled') {
        selectedData = attachDataByCanonicalKey(
          selectedData,
          pricebooksResult.value.pricebooks,
          'pricebooks',
          (item) => item?.pricebooks || []
        );
      } else {
        fetchErrors.push({ endpoint: 'Pricebooks', reason: pricebooksResult.reason.message });
      }
    } else {
      // CPQ Path
      const [rulesResult, pricebooksResult, priceModelsResult, consumptionResult, discountsResult] = relatedResults;

      if (rulesResult.status === 'fulfilled') {
        selectedData = attachDataByCanonicalKey(
          selectedData,
          rulesResult.value.rules,
          'productRules',
          (item) => item?.productRules || []
        );
      } else {
        fetchErrors.push({ endpoint: 'Product Rules', reason: rulesResult.reason.message });
      }

      console.log('[CPQ] pricebooksResult', pricebooksResult);

      if (pricebooksResult.status === 'fulfilled') {
        selectedData = attachDataByCanonicalKey(
          selectedData,
          pricebooksResult.value.pricebooks,
          'pricebooks',
          (item) => item?.pricebooks || []
        );
      } else {
        fetchErrors.push({ endpoint: 'Pricebooks', reason: pricebooksResult.reason.message });
      }

      // ... and so on for the other CPQ results ...
      if (priceModelsResult.status === 'fulfilled') {
        selectedData = matchPricingModelsToProducts(selectedData, priceModelsResult.value.pricingModels);
      } else {
        fetchErrors.push({ endpoint: 'Price Models', reason: priceModelsResult.reason.message });
      }

      if (consumptionResult.status === 'fulfilled') {
        selectedData = matchConsumptionToProducts(selectedData, consumptionResult.value);
      } else {
        fetchErrors.push({ endpoint: 'Consumption', reason: consumptionResult.reason.message });
      }

      if (discountsResult.status === 'fulfilled') {
        selectedData = matchDiscountSchedulesToProducts(selectedData, discountsResult.value.discountSchedules);
      } else {
        fetchErrors.push({ endpoint: 'Discounts', reason: discountsResult.reason.message });
      }
    }

    // --- STAGE 3: Compare and Finalize ---
    const diffPromises = selectedData.map((sfProduct) => {
      const dbProduct = databaseData.find((db) => db.name === sfProduct.name);
      return compareProducts(sfProduct, dbProduct, false).catch((err) => ({
        error: true,
        message: `Failed to compare ${sfProduct.name}: ${err.message}`,
      }));
    });

    const diffResults = await Promise.allSettled(diffPromises);
    const processedDiffs = [];
    const comparisonErrors = [];
    diffResults.forEach((result) => {
      if (result.status === 'fulfilled' && result.value && !result.value.error) {
        processedDiffs.push(result.value.diff);
      } else {
        comparisonErrors.push(result.status === 'rejected' ? result.reason : result.value);
      }
    });

    return { data: processedDiffs, errors: comparisonErrors, fetchErrors };
  } catch (error) {
    console.error('Critical Error in fetchAndProcessReviewData:', error);
    throw error; // Re-throw the already formatted error
  }
};

// In src/services/productDataService.js

// ... (keep the existing fetchAndProcessReviewData function)

// ===================================================================
// == DEPLOYMENT WORKFLOW FUNCTIONS
// ===================================================================

/**
 * DEPLOY - STEP 1: Fetches all product versions from the internal database for a given org.
 * This is called by the main Deploy/index.js component.
 *
 * @param {string} orgId - The ID of the organization.
 * @returns {Promise<object>} An object containing the products or an error.
 */
export const getDeployableProductsFromDB = async (orgId) => {
  if (!orgId) {
    return { products: [], error: 'Organization ID is required.' };
  }
  try {
    // The 'isDeploy' flag might tell the backend to fetch all versions, not just the latest.
    const response = await getProductsFromDb(orgId, [], true);
    if (response?.error) {
      throw new Error(response.message || 'Failed to fetch products from database.');
    }
    return { products: response?.products || [] };
  } catch (error) {
    console.error('Error in getDeployableProductsFromDB:', error);
    return { products: [], error: error.message };
  }
};

/**
 * DEPLOY - STEP 2: Fetches the full, related data for a selection of products from Salesforce.
 * This is called by the DeployReview component.
 *
 * @param {string} orgId - The ID of the target Salesforce org.
 * @param {Array<object>} selectedDbProducts - The array of product objects selected by the user from the DB.
 * @param {boolean} isRevenue - Flag to determine if it's an RC or CPQ org.
 * @returns {Promise<object>} An object containing the enriched Salesforce products or an error.
 */
export const fetchSalesforceStateForDeploy = async (orgId, selectedDbProducts, isRevenue) => {
  // Guard clause: Nothing to fetch if no products were selected.
  if (!orgId || !selectedDbProducts || !selectedDbProducts.length) {
    return { sfData: [], fetchErrors: [] };
  }

  try {
    // --- STEP 1: Fetch the Deep Product Model Data from Salesforce ---
    // This is now the single, essential first step.
    // The getRCProductModel/getCPQProductData functions are responsible for finding the
    // SF counterparts to the selectedDbProducts and fetching their full model.
    let sfDataWithModels;
    try {
      console.log('[fetchSalesforceStateForDeploy]', selectedDbProducts);
      const modelFetcher = isRevenue
        ? getRCProductModel(orgId, selectedDbProducts, false) // useExisting: false to force SF fetch
        : getCPQProductData(orgId, selectedDbProducts, false);

      // The response from these functions is the deeply nested product model data.
      const response = await modelFetcher;
      console.log('[fetchSalesforceStateForDeploy] response', response);
      sfDataWithModels = response?.products; // The response is the array of products directly
    } catch (modelError) {
      console.error('Critical error fetching deep product model data from Salesforce:', modelError);
      return {
        sfData: [],
        fetchErrors: [{ endpoint: 'Product Model Data', reason: modelError.message }],
      };
    }

    // If no products were found in Salesforce, we can stop here.
    if (!sfDataWithModels || sfDataWithModels.length === 0) {
      console.warn('No matching products found in the target Salesforce org.');
      return { sfData: [], fetchErrors: [] };
    }

    console.log('[sfDataWithModels]', sfDataWithModels);
    console.log('[isRevenueDeploy]', isRevenue);

    // --- STEP 2: Fetch All Other Related Data in Parallel ---
    // We use the live Salesforce data (`sfDataWithModels`) for these subsequent calls.
    const relatedDataPromises = isRevenue
      ? [getRCProductAttributes(orgId, sfDataWithModels), getRCPricebooks(orgId, sfDataWithModels)]
      : [
          getCPQProductRules(orgId, sfDataWithModels),
          getCPQPricebooks(orgId, sfDataWithModels),
          getCPQPriceModel(orgId, sfDataWithModels),
          getCPQConsumptionSchedule(orgId, sfDataWithModels),
          getCPQDiscountSchedule(orgId, sfDataWithModels),
        ];

    const relatedResults = await Promise.allSettled(relatedDataPromises);
    const fetchErrors = [];

    // --- STEP 3: Attach Related Data to the `sfDataWithModels` ---
    let finalSfData = sfDataWithModels;

    if (isRevenue) {
      const [attributesResult, pricebooksResult] = relatedResults;
      if (attributesResult.status === 'fulfilled') {
        const revenueData = attributesResult.value.attributes || [];
        const revenueMap = new Map(revenueData.map((p) => [p.productName, p]));
        finalSfData = finalSfData.map((sfProduct) => {
          const match = revenueMap.get(sfProduct.name);
          return match ? { ...sfProduct, related_data: { ...(sfProduct.related_data || {}), ...match } } : sfProduct;
        });
      } else {
        fetchErrors.push({ endpoint: 'RC Attributes', reason: attributesResult.reason.message });
      }

      console.log('[RevenueDeploy] pricebooksResult', pricebooksResult);

      if (pricebooksResult.status === 'fulfilled') {
        finalSfData = attachDataByCanonicalKey(
          finalSfData,
          pricebooksResult.value.pricebooks,
          'pricebooks',
          (item) => item?.pricebooks || []
        );
      } else {
        fetchErrors.push({ endpoint: 'Pricebooks', reason: pricebooksResult.reason.message });
      }
    } else {
      // CPQ Path
      const [rulesResult, pricebooksResult, priceModelsResult, consumptionResult, discountsResult] = relatedResults;

      if (rulesResult.status === 'fulfilled') {
        finalSfData = attachDataByCanonicalKey(
          finalSfData,
          rulesResult.value.rules,
          'productRules',
          (item) => item?.productRules || []
        );
      } else {
        fetchErrors.push({ endpoint: 'Product Rules', reason: rulesResult.reason.message });
      }

      console.log('[fetchSalesforceStateForDeploy] pricebooksResult', pricebooksResult);
      if (pricebooksResult.status === 'fulfilled') {
        finalSfData = attachDataByCanonicalKey(
          finalSfData,
          pricebooksResult.value.pricebooks,
          'pricebooks',
          (item) => item?.pricebooks || []
        );
      } else {
        fetchErrors.push({ endpoint: 'Pricebooks', reason: pricebooksResult.reason.message });
      }

      if (priceModelsResult.status === 'fulfilled') {
        finalSfData = matchPricingModelsToProducts(finalSfData, priceModelsResult.value.pricingModels);
      } else {
        fetchErrors.push({ endpoint: 'Price Models', reason: priceModelsResult.reason.message });
      }

      if (consumptionResult.status === 'fulfilled') {
        finalSfData = matchConsumptionToProducts(finalSfData, consumptionResult.value);
      } else {
        fetchErrors.push({ endpoint: 'Consumption', reason: consumptionResult.reason.message });
      }

      if (discountsResult.status === 'fulfilled') {
        finalSfData = matchDiscountSchedulesToProducts(finalSfData, discountsResult.value.discountSchedules);
      } else {
        fetchErrors.push({ endpoint: 'Discounts', reason: discountsResult.reason.message });
      }
    }

    console.log('[fetchSalesforceStateForDeploy] finalSfData', finalSfData);

    // Return the fully enriched Salesforce data, ready for comparison.
    return { sfData: finalSfData, fetchErrors };
  } catch (error) {
    console.error('Critical Error in fetchSalesforceStateForDeploy:', error);
    return { sfData: [], fetchErrors: [{ endpoint: 'Global', reason: error.message }] };
  }
};

/**
 * DEPLOY - STEP 3: Compares the DB products against the fetched SF state and returns the diffs.
 * This is also called by the DeployReview component.
 *
 * @param {Array<object>} dbProducts - The array of product objects selected by the user.
 * @param {Array<object>} sfProducts - The array of enriched product objects from Salesforce.
 * @returns {Promise<object>} An object containing the diffs and any comparison errors.
 */
export const compareForDeploy = async (dbProducts, sfProducts) => {
  if (!dbProducts || dbProducts.length === 0) {
    return { diffs: [], errors: [] };
  }

  const diffPromises = dbProducts.map((dbProduct) => {
    const sfProduct = sfProducts.find((sf) => sf.name === dbProduct.name);
    // For DEPLOY, DB product is "new" (database_product) and SF is "old" (sf_product).
    return compareProducts(dbProduct, sfProduct, true).catch((err) => ({
      error: true,
      message: `Failed to compare ${dbProduct.name}: ${err.message}`,
    }));
  });

  const diffResults = await Promise.allSettled(diffPromises);
  const processedDiffs = [];
  const comparisonErrors = [];

  diffResults.forEach((result) => {
    if (result.status === 'fulfilled' && result.value && !result.value.error) {
      processedDiffs.push(result.value.diff);
    } else {
      const error = result.status === 'rejected' ? result.reason : result.value;
      comparisonErrors.push(error);
    }
  });

  return { diffs: processedDiffs, errors: comparisonErrors };
};
